import {
  useState, useEffect, useRef, type ReactNode
} from 'react';
import {
  API_BASE_URL
} from '../config';
import {
  trackEvent
} from '../analytics/posthog';
import {
  validateNewInstanceLabel
} from '../utils/integrationLabels';
import {
  AccountNameField
} from './AccountNameField';
import {
  PairingModalShell
} from './pairing/PairingModalShell';
import {
  isMobileBrowser
} from '../utils/inAppBrowser';

// The backend serves the OAuth callback HTML from `API_BASE_URL`, which posts a
// `${name}-oauth` message back to this window. Only accept messages from that
// origin — any other window able to reach the opener could otherwise forge a
// "connected" signal. (The popup-closed status re-check below is the second
// line of defence.)
const EXPECTED_CALLBACK_ORIGIN = (() => {
  try {
    return new URL(API_BASE_URL).origin;
  } catch {
    return null;
  }
})();

interface DiscoveredUrlEvents {
  modalOpened: string;
  urlSubmitted: string;
  connected: string;
  connectionFailed: string;
  modalClosed: string;
}

interface DiscoveredUrlService {
  connect: (
    resourceUrl: string,
    label?: string,
    returnMode?: 'popup' | 'redirect',
  ) => Promise<{ authorization_url: string }>;
  getStatus: () => Promise<{ connected: boolean }>;
}

interface DiscoveredUrlPairingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  // Stable integration name (e.g. "zoho"); drives the popup name and the
  // `${name}-oauth` postMessage type the backend callback posts back.
  name: string;
  displayName: string;
  // Provider-specific intro copy rendered above the URL field.
  instructions: ReactNode;
  urlLabel: string;
  urlPlaceholder: string;
  service: DiscoveredUrlService;
  events: DiscoveredUrlEvents;
  // Labels of accounts already connected for this integration. When non-empty,
  // this is an "add another account" flow: a required, unique account name is
  // collected and passed to connect so a second instance is created instead of
  // overwriting the first.
  existingLabels?: string[] | undefined;
}

export function DiscoveredUrlPairingModal({
  isOpen,
  onClose,
  onSuccess,
  name,
  displayName,
  instructions,
  urlLabel,
  urlPlaceholder,
  service,
  events,
  existingLabels = [],
}: DiscoveredUrlPairingModalProps) {
  const isAddingAnother = existingLabels.length > 0;
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const modalOpenTracked = useRef(false);
  const popupRef = useRef<Window | null>(null);
  const pollIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (isOpen && !modalOpenTracked.current) {
      trackEvent(events.modalOpened);
      modalOpenTracked.current = true;
    }
    if (!isOpen) {
      modalOpenTracked.current = false;
      setUrl('');
      setLabel('');
      setError(null);
      setSubmitting(false);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }
  }, [isOpen]);

  // Listen for the popup's postMessage on a successful authorize-callback.
  // Re-subscribe whenever the callbacks change so we don't capture a stale
  // closure around the first render's onClose/onSuccess.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        EXPECTED_CALLBACK_ORIGIN !== null
        && event.origin !== EXPECTED_CALLBACK_ORIGIN
      ) {
        // Posted from somewhere other than our backend callback — ignore.
        return;
      }
      if (event.data?.type !== `${name}-oauth`) return;
      if (event.data.success) {
        finalizeConnected();
      } else {
        const errorMsg = String(event.data.message ?? 'Connection failed').replace(/^error:/, '');
        trackEvent(events.connectionFailed, { error: errorMsg });
        setError(errorMsg);
        setSubmitting(false);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onClose, onSuccess]);

  const finalizeConnected = () => {
    trackEvent(events.connected);
    setError(null);
    setSubmitting(false);
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    onClose();
    onSuccess();
  };

  const handleClose = () => {
    trackEvent(events.modalClosed);
    setError(null);
    setSubmitting(false);
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    onClose();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = url.trim();
    if (!trimmed) {
      setError('Paste the server URL to continue.');
      return;
    }

    const trimmedLabel = label.trim();
    if (isAddingAnother) {
      const labelError = validateNewInstanceLabel(existingLabels, label);
      if (labelError) {
        setError(labelError);
        return;
      }
    }

    setSubmitting(true);
    setError(null);
    trackEvent(events.urlSubmitted);

    // Mobile browsers (iOS Safari especially) can't complete the popup OAuth
    // handshake — window.opener is severed and window.close() no-ops, so the
    // connect never reports back (GET-8975, mirroring GET-8963). Navigate the
    // top-level tab to the auth URL instead; the backend callback 302s us back
    // to /integrations, where SettingsPage reads the result on the fresh mount.
    const mobile = isMobileBrowser();

    try {
      const { authorization_url } = await service.connect(
        trimmed,
        trimmedLabel || undefined,
        mobile ? 'redirect' : undefined,
      );

      if (mobile) {
        window.location.assign(authorization_url);
        return;
      }

      // Desktop: open the provider's authorize page in a centered popup. The
      // callback HTML posts a `${name}-oauth` message back here and closes
      // itself; we also poll status after the popup closes in case the user
      // dismissed it.
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      popupRef.current = window.open(
        authorization_url,
        `${name}-oauth`,
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`,
      );
      if (!popupRef.current) {
        setError('Popup was blocked. Please allow popups and try again.');
        setSubmitting(false);
        return;
      }

      pollIntervalRef.current = window.setInterval(() => {
        if (popupRef.current?.closed) {
          clearInterval(pollIntervalRef.current!);
          pollIntervalRef.current = null;
          service.getStatus().then((s) => {
            if (s.connected) finalizeConnected();
            else setSubmitting(false);
          }).catch(() => setSubmitting(false));
        }
      }, 500);
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || `Failed to start ${displayName} connection.`;
      trackEvent(events.connectionFailed, { error: message });
      setError(message);
      setSubmitting(false);
    }
  };

  return (
    <PairingModalShell
      isOpen={isOpen}
      onClose={handleClose}
      title={isAddingAnother ? `Add another ${displayName} account` : `Connect ${displayName}`}
      closeAriaLabel="Close"
    >
      <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4">
        <div className="text-sm text-gray-600 space-y-3">
          {instructions}
        </div>

        <label htmlFor={`${name}-resource-url`} className="block text-sm font-medium text-gray-700">
          {urlLabel}
        </label>
        <input
          id={`${name}-resource-url`}
          type="url"
          autoComplete="off"
          spellCheck={false}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder={urlPlaceholder}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          disabled={submitting}
        />

        {isAddingAnother && (
          <AccountNameField
            id={`${name}-account-label`}
            value={label}
            onChange={setLabel}
            existingLabels={existingLabels}
            inputClassName="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            disabled={submitting}
          />
        )}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-2">
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            disabled={submitting || !url.trim()}
          >
            {submitting
              ? 'Connecting...'
              : isAddingAnother
                ? 'Add account'
                : `Authorize with ${displayName}`}
          </button>
        </div>
      </form>
    </PairingModalShell>
  );
}
